import React, { useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import { App } from '@myua/kit';
import { DroppedData, flatten, isNotOSFile, useDropzone } from '@myua/dropzone';
import { useDispatch, useSelector } from 'react-redux';
import * as actions from '../actions';
import { getEnabled, getModalOpen, getUploadDestinationPath } from '../selectors';
import { useUploader } from '../useUploader';
import { getEmptyCatalogUploadVisible } from '../emptyCatalog';

const UploadIconLarge = () => (
  <svg width="56" height="56" viewBox="0 0 42 42" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      opacity="0.4"
      d="M29.4 15.75H12.6C7 15.75 3.5 19.25 3.5 24.85V29.3825C3.5 35 7 38.5 12.6 38.5H29.3825C34.9825 38.5 38.4825 35 38.4825 29.4V24.85C38.5 19.25 35 15.75 29.4 15.75Z"
      fill="#1862D1"
    />
    <path
      d="M27.79 9.74703L21.9275 3.88453C21.42 3.37703 20.58 3.37703 20.0725 3.88453L14.21 9.74703C13.7025 10.2545 13.7025 11.0945 14.21 11.602C14.7175 12.1095 15.5575 12.1095 16.065 11.602L19.6875 7.97953V26.687C19.6875 27.4045 20.2825 27.9995 21 27.9995C21.7175 27.9995 22.3125 27.4045 22.3125 26.687V7.97953L25.935 11.602C26.1975 11.8645 26.53 11.987 26.8625 11.987C27.195 11.987 27.5275 11.8645 27.79 11.602C28.315 11.0945 28.315 10.272 27.79 9.74703Z"
      fill="#1862D1"
    />
  </svg>
);

const dragHasFiles = (dt: DataTransfer | null): boolean =>
  Boolean(dt && Array.from(dt.types).includes('Files'));

export const UploadDropOverlay: React.FC = () => {
  const dispatch = useDispatch();
  // The empty catalog has its own dropzone (EmptyCatalogUpload): the page overlay would
  // cover it and upload past its destination picker.
  const emptyCatalogUpload = useSelector(getEmptyCatalogUploadVisible);
  const enabled = useSelector(getEnabled) && !emptyCatalogUpload;
  const modalOpen = useSelector(getModalOpen);
  const destinationPath = useSelector(getUploadDestinationPath);
  const { upload } = useUploader();
  const { modal } = App.useApp();

  const ref = useRef<HTMLDivElement>(null);
  const [windowDragActive, setWindowDragActive] = useState(false);

  useEffect(() => {
    if (!enabled || modalOpen) {
      setWindowDragActive(false);
      return;
    }

    let counter = 0;

    const onEnter = (event: DragEvent) => {
      if (!dragHasFiles(event.dataTransfer)) return;
      counter += 1;
      setWindowDragActive(true);
    };
    const onLeave = (event: DragEvent) => {
      if (!dragHasFiles(event.dataTransfer)) return;
      counter -= 1;
      if (counter <= 0) {
        counter = 0;
        setWindowDragActive(false);
      }
    };
    const onDrop = () => {
      counter = 0;
      setWindowDragActive(false);
    };

    window.addEventListener('dragenter', onEnter);
    window.addEventListener('dragleave', onLeave);
    window.addEventListener('drop', onDrop);
    return () => {
      window.removeEventListener('dragenter', onEnter);
      window.removeEventListener('dragleave', onLeave);
      window.removeEventListener('drop', onDrop);
    };
  }, [enabled, modalOpen]);

  const handleDrop = (data: DroppedData) => {
    const flat = flatten(data);
    const files = Object.values(flat).flat().filter(isNotOSFile);
    if (files.length === 0) return;

    const destText = destinationPath.length > 0 ? destinationPath.join(' → ') : 'selected category';
    const noun = files.length === 1 ? 'file' : 'files';

    modal.confirm({
      title: 'Upload files',
      content: (
        <span>
          Do you really want to upload <b>{files.length}</b> {noun} to <b>{destText}</b>?
        </span>
      ),
      okText: 'Yes',
      cancelText: 'No',
      icon: null,
      onOk: (close) => {
        dispatch(actions.modalOpened({ pendingUpload: true }));
        void upload(files);
        close();
      },
    });
  };

  const { getRootProps, isDragActive } = useDropzone<HTMLDivElement>({
    handler: handleDrop,
    disabled: !enabled || modalOpen,
    ref,
  });

  if (!enabled || modalOpen || !windowDragActive) return null;

  return (
    <div
      ref={ref}
      className={classNames('al-upload-drop-overlay', {
        'al-upload-drop-overlay_active': isDragActive,
      })}
      {...getRootProps()}
    >
      <div className="al-upload-drop-overlay__inner">
        <div className="al-upload-drop-overlay__icon">
          <UploadIconLarge />
        </div>
        <div className="al-upload-drop-overlay__title">Drop files here to upload</div>
        {destinationPath.length > 0 && (
          <div className="al-upload-drop-overlay__destination">
            {destinationPath.map((name, idx) => (
              <React.Fragment key={idx}>
                {idx > 0 && <span className="al-upload-drop-overlay__destination-arrow">→</span>}
                <span className="al-upload-drop-overlay__destination-item">{name}</span>
              </React.Fragment>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
